import { type ReactNode } from 'react';

import { cn } from '../lib/utils.js';

/**
 * Strongly-styled section container shared across detail panels and list
 * pages. Each card carries:
 *   1. A colored top accent bar (1px) for visual anchoring
 *   2. A tinted header band with an icon chip + uppercase title
 *   3. A pronounced shadow + colored border so the card visibly lifts off
 *      a tinted page background
 *
 * Section identity is carried by color, not just text labels — the panel /
 * page reads as a dashboard of distinct modules instead of a continuous form.
 *
 * Originally lived inside ActivityDetailsSidebar; pulled here so the same
 * vocabulary can be reused on ActivitiesPage (Calendar / Table tabs) and any
 * future surface that wants the visual rhythm.
 */

export type SectionAccent =
  | 'slate'
  | 'indigo'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'purple';

export const SECTION_ACCENTS: Record<SectionAccent, {
  card: string;
  header: string;
  title: string;
  iconChip: string;
  iconColor: string;
  topBar: string;
}> = {
  slate:   { card: 'border-slate-300',   header: 'bg-slate-50 border-slate-200',     title: 'text-slate-800',   iconChip: 'bg-slate-100',   iconColor: 'text-slate-600',   topBar: 'bg-slate-500' },
  indigo:  { card: 'border-indigo-300',  header: 'bg-indigo-50 border-indigo-200',   title: 'text-indigo-800',  iconChip: 'bg-indigo-100',  iconColor: 'text-indigo-600',  topBar: 'bg-indigo-500' },
  blue:    { card: 'border-blue-300',    header: 'bg-blue-50 border-blue-200',       title: 'text-blue-800',    iconChip: 'bg-blue-100',    iconColor: 'text-blue-600',    topBar: 'bg-blue-500' },
  emerald: { card: 'border-emerald-300', header: 'bg-emerald-50 border-emerald-200', title: 'text-emerald-800', iconChip: 'bg-emerald-100', iconColor: 'text-emerald-600', topBar: 'bg-emerald-500' },
  amber:   { card: 'border-amber-300',   header: 'bg-amber-50 border-amber-200',     title: 'text-amber-900',   iconChip: 'bg-amber-100',   iconColor: 'text-amber-700',   topBar: 'bg-amber-500' },
  rose:    { card: 'border-rose-300',    header: 'bg-rose-50 border-rose-200',       title: 'text-rose-800',    iconChip: 'bg-rose-100',    iconColor: 'text-rose-600',    topBar: 'bg-rose-500' },
  purple:  { card: 'border-purple-300',  header: 'bg-purple-50 border-purple-200',   title: 'text-purple-800',  iconChip: 'bg-purple-100',  iconColor: 'text-purple-600',  topBar: 'bg-purple-500' },
};

export interface SectionCardProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  /** Color palette for the card. Defaults to slate (neutral). */
  accent?: SectionAccent;
  /** Override default `p-3` body padding. Pass `p-0` for full-bleed children. */
  bodyClassName?: string;
  /** Right-aligned slot in the header band (buttons, badges, etc.) */
  action?: ReactNode;
  /** Extra classes on the outer card div. */
  className?: string;
}

export function SectionCard({
  title,
  icon,
  children,
  accent = 'slate',
  bodyClassName,
  action,
  className,
}: SectionCardProps) {
  const a = SECTION_ACCENTS[accent];
  return (
    <div className={cn(
      "bg-white rounded-lg border shadow-md overflow-hidden",
      a.card,
      className,
    )}>
      <div className={cn("h-1", a.topBar)} aria-hidden />
      <div className={cn(
        "flex items-center justify-between gap-2 px-3 py-2 border-b",
        a.header,
      )}>
        <div className={cn("flex items-center gap-2 min-w-0", a.title)}>
          {icon && (
            <span className={cn(
              "shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-md ring-1 ring-inset ring-black/5",
              a.iconChip,
              a.iconColor,
            )}>
              {icon}
            </span>
          )}
          <h3 className="text-xs font-extrabold uppercase tracking-wider truncate">
            {title}
          </h3>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className={cn("p-3", bodyClassName)}>
        {children}
      </div>
    </div>
  );
}
